import { createFileRoute, Link } from "@tanstack/react-router";
import { absUrl, canonicalLink, facebookFeedMeta, pathFromMatch, keywordsWithLocal } from "@/lib/seo";
import { homeHeadScripts } from "@/lib/cityJsonLd";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";

import { LetsTalkForm } from "@/components/LetsTalkForm";
import glampingTentAsset from "@/assets/glamping-tent-hill-country.png.asset.json";
import { HomeHeroBanner } from "@/components/HomeHeroBanner";
import { track } from "@/lib/analytics";

import { Phone, Check, ArrowRight, MapPin, Facebook } from "lucide-react";
import { useState } from "react";
import { Lightbox, type LightboxImage } from "@/components/Lightbox";
import heroImg from "@/assets/original/hero-shade.jpg";
// What We Do imagery
import marineBoat from "@/assets/marine-canvas-boat.jpg";

// Featured Projects gallery imagery
import porch from "@/assets/original/stock-porch.jpg";

import bradzoilBanner from "@/assets/awning-bradzoil-banner.jpg";
import shopFrontAsset from "@/assets/shop-front.png.asset.json";
import homeBannerAsset from "@/assets/home-hero-banner.png.asset.json";
import { assetSrcSet, assetUrl, firstAssetUrl } from "@/lib/asset-url";
// Responsive, compressed variants of the home banner (WebP + JPEG fallback).
import hb640w from "@/assets/home-hero-banner-640.webp.asset.json";
import hb960w from "@/assets/home-hero-banner-960.webp.asset.json";
import hb1280w from "@/assets/home-hero-banner-1280.webp.asset.json";
import hb1536w from "@/assets/home-hero-banner-1536.webp.asset.json";
import hb640j from "@/assets/home-hero-banner-640.jpg.asset.json";
import hb960j from "@/assets/home-hero-banner-960.jpg.asset.json";
import hb1280j from "@/assets/home-hero-banner-1280.jpg.asset.json";
import hb1536j from "@/assets/home-hero-banner-1536.jpg.asset.json";
// Some banner asset pointers can be missing/empty (failed upload). Build the
// srcsets defensively and always keep a usable fallback URL for the <img>.
const homeBannerWebpSrcset = assetSrcSet([
  [hb640w, 640],
  [hb960w, 960],
  [hb1280w, 1280],
  [hb1536w, 1536],
]);
const homeBannerJpegSrcset = assetSrcSet([
  [hb640j, 640],
  [hb960j, 960],
  [hb1280j, 1280],
  [hb1536j, 1536],
]);
// Prefer the original PNG; fall back to the largest available JPEG/WebP variant.
const homeBannerFallback = firstAssetUrl(hb1536j, hb1280j, hb960j, hb640j, hb1536w, hb1280w, hb960w, hb640w);
const homeBanner = firstAssetUrl(homeBannerAsset) || homeBannerFallback;




export const Route = createFileRoute("/")({
  head: (ctx) => {
    const ogImage = absUrl(heroImg);
    // Title kept ≤60 chars so Google doesn't truncate it in the SERP.
    // Leads with the highest-volume term ("custom awnings") + region.
    const seoTitle = "Custom Awnings & Shade Structures in Texas | Texas Made Shade";
    const ogTitle = "Texas Made Shade — Awnings, Outdoor Curtains & Sail Shades";
    const ogDesc = "Texas awning company. Commercial awnings Texas, awnings San Antonio, awnings Austin, outdoor curtains, sail shades & marine canvas — built in Seguin since 1992.";
    return {
      links: [
        canonicalLink(pathFromMatch(ctx)),
      ],
      meta: [
        ...facebookFeedMeta({
          title: seoTitle,
          description: "Custom awnings, shade sails, outdoor curtains, patio covers and boat covers, built in Seguin and installed across Texas for homes and businesses. Free quotes: (830) 390-9588.",
          image: ogImage,
          socialTitle: ogTitle,
          socialDescription: ogDesc,
        }, ctx),

        { property: "og:image:type", content: "image/jpeg" },
        { name: "keywords", content: keywordsWithLocal("Awning Company San Antonio, awning company San Antonio TX, San Antonio awning company, awning companies San Antonio, Awnings Austin TX, awnings Austin, awning company Austin, Sail Shades San Antonio, shade sails San Antonio, sail shades San Antonio TX, awnings San Antonio, outdoor curtains San Antonio, outdoor curtains Austin, commercial awnings San Antonio, commercial awnings Austin, commercial awnings Texas, residential awnings San Antonio, patio covers San Antonio, patio covers Austin, pergola covers, retractable awnings, metal awnings, canvas awnings, Texas awning company, Texas Made Shade") },
      ],
      scripts: homeHeadScripts(),
    };
  },
  component: Index,
});


const FB_URL = "https://www.facebook.com/profile.php?id=61556233022173";

function Index() {
  const galleryImages: LightboxImage[] = [
    { src: heroImg, alt: "Custom shade structure over a baseball field", label: "Ballfield Shade Structure" },
    {
      src: bradzoilBanner,
      alt: "Hand-painted Bradzoil 10 Minute Oil Change commercial banner awning fabricated and installed by Texas Made Shade",
      label: "Bradzoil 10 Minute Oil Change — Commercial Banner Awning",
    },
    { src: marineBoat, alt: "Marine canvas boat cover built in our Seguin shop", label: "Marine Canvas" },
    { src: porch, alt: "Patio and porch shade in the Texas Hill Country", label: "Patio & Porch Shade" },
  ];
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main id="main-content" tabIndex={-1}>
        <HomeHeroBanner variant="banner" />

{/* FOUR CATEGORIES */}
<section className="border-b-8 border-navy-deep bg-white py-14 lg:py-16">
  <div className="mx-auto max-w-7xl px-4 lg:px-8">
    <div className="text-center">
      <h2 className="font-display text-3xl font-bold uppercase text-navy-deep md:text-4xl">
        What are you looking for?
      </h2>
      <p className="mx-auto mt-3 max-w-2xl text-base text-navy-deep/75">
        Start with the kind of project you have. We&apos;ll point you to the right shade,
        cover or outdoor living system.
      </p>
    </div>
    <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {[
        {
          to: "/residential-awnings",
          label: "Residential",
          blurb: "Patio & window awnings, sail shades, porch and pool shade, HVAC & misting for your home.",
        },
        {
          to: "/hill-country-series",
          label: "Outdoor Living",
          blurb: "Retractable awnings, motorized screens, louvered pergolas, lighting, misting & HVAC.",
        },
        {
          to: "/commercial-awnings",
          label: "Commercial",
          blurb: "Storefront and restaurant awnings, walkway canopies, schools and churches.",
        },
        {
          to: "/covers-tops",
          label: "Specialty",
          blurb: "Boat, RV, trailer and gear covers, ag canvas, welding and custom fabrication.",
        },
      ].map((c) => (
        <li key={c.label}>
          <Link
            to={c.to}
            className="group flex h-full flex-col justify-between border-2 border-navy-deep bg-cream p-5 shadow-card transition hover:-translate-y-1 hover:bg-navy-deep hover:text-cream"
          >
            <span className="font-display text-xl font-bold uppercase tracking-wide">
              {c.label}
            </span>
            <span className="mt-2 text-sm leading-snug opacity-80">{c.blurb}</span>
            <span className="mt-4 inline-flex items-center gap-2 font-display text-xs uppercase tracking-[0.2em] text-red group-hover:text-white">
              Explore <ArrowRight className="h-4 w-4" />
            </span>
          </Link>
        </li>
      ))}
    </ul>
    <div className="mt-8 flex flex-wrap justify-center gap-3">
      <Link
        to="/products"
        className="inline-flex items-center justify-center gap-2 border-2 border-navy-deep px-6 py-3 font-display text-sm uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-cream"
      >
        See every product we build <ArrowRight className="h-4 w-4" />
      </Link>
      <Link
        to="/series"
        className="inline-flex items-center justify-center gap-2 border-2 border-navy-deep/30 px-6 py-3 font-display text-sm uppercase tracking-wider text-navy-deep transition hover:border-navy-deep"
      >
        Browse our signature series
      </Link>
    </div>
  </div>
</section>

{/* HERO */}
<section className="relative overflow-hidden bg-navy-deep text-white">
  <div className="absolute inset-0">
    <img src={heroImg} alt="Custom Texas Made Shade structure over a baseball field" className="h-full w-full object-cover" width={1920} height={1080} loading="lazy" fetchPriority="low" decoding="async" />
    <div className="absolute inset-0 bg-gradient-to-r from-navy-deep/85 via-navy-deep/45 to-transparent" />
  </div>
  <div className="relative mx-auto grid max-w-7xl gap-10 px-4 py-12 sm:py-16 md:py-20 lg:grid-cols-12 lg:px-8 lg:py-24">
    <div className="lg:col-span-8">
      <div className="inline-flex items-center gap-2 bg-red px-4 py-1.5 font-display text-xs tracking-[0.3em] text-white">
        CUSTOM AWNINGS • RETRACTABLE SHADE • OUTDOOR COMFORT
      </div>

      <h1 className="mt-4 font-display text-3xl font-bold leading-[0.95] sm:mt-6 sm:text-5xl md:text-6xl lg:text-7xl">
        Shade. Outdoor Comfort.<br />
        <span className="text-white">Built in Texas.</span>
      </h1>

      <p className="mt-5 max-w-2xl text-base text-white/85 sm:text-lg md:text-xl">
        The Texas heat is brutal — your patio doesn&apos;t have to be. We design, weld and sew custom{" "}
        <Link to="/awnings" className="underline decoration-red decoration-2 underline-offset-4 hover:text-white">awnings</Link>,{" "}
        <Link to="/pergola-covers" className="underline decoration-red decoration-2 underline-offset-4 hover:text-white">patio covers</Link>,{" "}
        <Link to="/sail-shades" className="underline decoration-red decoration-2 underline-offset-4 hover:text-white">sail shades</Link> and{" "}
        <Link to="/boat-covers" className="underline decoration-red decoration-2 underline-offset-4 hover:text-white">marine canvas</Link> in our Seguin shop.
        Our own crew installs it across San Antonio, Austin, the Hill Country and the Gulf Coast — and we&apos;re still here when you need a warranty fix five summers from now.
      </p>

      <div className="mt-6 flex flex-col gap-3 sm:mt-8 sm:flex-row sm:flex-wrap sm:gap-4">
        <a href="#lets-talk" className="inline-flex items-center justify-center gap-2 bg-red px-6 py-3.5 font-display text-base uppercase tracking-wider text-white shadow-bold transition hover:bg-red-deep sm:px-8 sm:py-4">
          <ArrowRight className="h-5 w-5" /> Get a Free Estimate
        </a>
        <a href="tel:+18303909588" className="inline-flex items-center justify-center gap-2 bg-white px-6 py-3.5 font-display text-base uppercase tracking-wider text-navy-deep shadow-bold transition hover:bg-cream sm:px-8 sm:py-4">
          <Phone className="h-5 w-5" /> Call +1 (830) 390-9588
        </a>
      </div>
      <p className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/80">
        <span className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-red" /> 30+ years, 1,000+ Texas projects</span>
        <span className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-red" /> Free on-site measure</span>
        <span className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-red" /> Same-business-day response</span>
      </p>
    </div>
  </div>
  <div className="stripe-bar absolute bottom-0 left-0 h-2 w-full" />
</section>

        {/* PROJECT PHOTOS */}
        <section className="bg-white py-10 lg:py-12">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <div className="mb-6 flex flex-wrap items-end justify-between gap-6">
              <div>
                <h2 className="font-display text-3xl font-bold uppercase leading-[0.9] text-navy-deep md:text-4xl">
                  Recent Texas Projects
                </h2>
                <p className="mt-2 max-w-2xl text-base text-navy-deep/75">
                  Fabricated and installed by our own crew — from storefront awnings for longtime
                  partners like{" "}
                  <a
                    href="http://www.bradzoil.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline decoration-red/60 underline-offset-2 hover:text-red"
                  >
                    Bradzoil
                  </a>{" "}
                  to marine canvas and Hill Country patio shade.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <Link
                  to="/projects"
                  className="inline-flex items-center gap-2 border-b-2 border-navy-deep pb-1 font-display text-sm uppercase tracking-[0.2em] text-navy-deep transition hover:border-red hover:text-red"
                >
                  View Full Portfolio <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href={FB_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track("facebook_click", { target: FB_URL, metadata: { placement: "home_gallery" } })}
                  className="inline-flex items-center gap-2 border border-navy-deep/20 px-3 py-2 font-display text-xs uppercase tracking-wider text-navy-deep transition hover:border-[#1877F2] hover:text-[#1877F2]"
                  aria-label="Follow our day-to-day work on Facebook (opens in a new tab)"
                >
                  <Facebook className="h-4 w-4" aria-hidden="true" /> Daily work on Facebook
                </a>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {galleryImages.map((p, i) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => setLightboxIndex(i)}
                  aria-label={`Enlarge ${p.label}`}
                  className="relative block aspect-[16/9] overflow-hidden text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-red"
                >
                  <img src={p.src} alt={p.alt} loading="lazy" decoding="async" width={1200} height={675} className="h-full w-full object-cover transition duration-300 hover:scale-105" />
                  <span className="absolute inset-0 bg-gradient-to-t from-navy-deep/90 via-transparent to-transparent" />
                  <span className="absolute bottom-4 left-4 block font-display text-lg text-white md:text-xl">
                    {p.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
          <Lightbox images={galleryImages} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onIndexChange={setLightboxIndex} />
        </section>

        {/* WHY CHOOSE US — one section, shop photo + three reasons */}
        <section className="border-y-8 border-navy-deep bg-navy-deep py-16 text-cream lg:py-20">
          <div className="mx-auto grid max-w-7xl gap-12 px-4 lg:grid-cols-2 lg:gap-16 lg:px-8">
            <div>
              <p className="font-display text-xs uppercase tracking-[0.3em] text-red">★ A Real Shop. A Real Crew.</p>
              <h2 className="mt-3 font-display text-4xl font-bold uppercase leading-none md:text-5xl">
                Why Texas Made Shade
              </h2>
              <p className="mt-5 max-w-[55ch] text-lg leading-relaxed text-cream/80">
                We&apos;re open Monday through Friday at our{" "}
                <Link to="/seguin" className="underline decoration-red decoration-2 underline-offset-4 hover:text-cream">Seguin</Link>{" "}
                fabrication shop — welders, sewing machines and inventory you can walk in and see.
              </p>
              <ul className="mt-8 space-y-6">
                {[
                  {
                    h: "Welded in Seguin",
                    p: "Every frame is welded in our own shop from marine-grade aluminum and structural steel.",
                  },
                  {
                    h: "Patterned & Sewn In-House",
                    p: "Canvas is measured on-site, cut and sewn here using Sunbrella® and Tempotest — no outsourced panels.",
                  },
                  {
                    h: "One Crew, Turnkey",
                    p: "Same crew from first measurement to last bolt, plus warranty work handled in our shop for years after.",
                  },
                ].map((b) => (
                  <li key={b.h} className="border-l-8 border-red pl-5">
                    <h3 className="font-display text-xl font-bold uppercase leading-tight md:text-2xl">{b.h}</h3>
                    <p className="mt-2 max-w-[45ch] text-base leading-relaxed text-cream/75">{b.p}</p>
                  </li>
                ))}
              </ul>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Texas+Made+Shade+%26+Covers%2C+959+East+Kingsbury+Street%2C+Seguin%2C+TX+78155"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-2 border-2 border-cream px-6 py-3 font-display text-sm uppercase tracking-wider text-cream transition hover:bg-cream hover:text-navy-deep"
              >
                <MapPin className="h-4 w-4" />
                959 E. Kingsbury St., Seguin TX
              </a>
            </div>
            <div className="self-start border-l-4 border-red bg-navy-deep/50 p-5">
              <img
                src={shopFrontAsset.url}
                alt="Texas Made Shade storefront at 959 E. Kingsbury St., Seguin, TX — red building with custom awnings and shade products on display"
                className="aspect-[4/3] w-full rounded-sm object-cover"
                loading="lazy"
                decoding="async"
              />
              <p className="mt-4 font-display text-xs uppercase tracking-widest text-red">
                Our Seguin storefront — stop by any weekday
              </p>
            </div>
          </div>
        </section>

        {/* QUOTE FORM */}
        <LetsTalkForm
          imageSrc={assetUrl(glampingTentAsset)}
          imageAlt="Custom canvas glamping tent with covered wooden deck porch in the Texas Hill Country, built by Texas Made Shade for outdoor hospitality and luxury camping"
        />

      </main>
      <SiteFooter />
    </div>
  );
}
